/**
 * `deriveMoveHoldDecider`: who a held move is waiting on, and why, for {@link
 * import("./MoveHoldBanner").MoveHoldBanner} (the mover) and {@link
 * import("./TablePausedBanner").TablePausedBanner} (bystanders), so a halted move doesn't read as
 * a skipped animation (`projects/overwatch-mid-move.md` 3.3).
 *
 * Read off the live `game_decision` map, the same truthful signal {@link
 * import("./resolutionWait").deriveResolutionWait} reads for a halted attack: the server posts one
 * decision per checkpoint halt, with the mover as `source` and the watcher as `who`, and a re-halt on
 * the next watcher replaces it. No new wire field. The map is fog-gated server-side, and a decider
 * this client's projected state cannot see is not named here either, so a concealed watcher stays
 * concealed.
 */
import { controlsCombatant, type EncounterState } from "@lancer-vtt/domain";
import { COVERING_FIRE_SPLIT_ID, OVERWATCH_CHECKPOINT_ID, isMoveCheckpointPromptId, moveCheckpointConsumerId } from "@lancer-vtt/rules";
import type { GameDecisionMessage } from "@lancer-vtt/shared";

/** The decider line a hold banner renders: `<deciderName> <why>`. */
export type MoveHoldDecider = {
  deciderName: string;
  /**
   * `threatens your start square` (Overwatch), `holds your move for Covering Fire` (a move held
   * partway for the shot, `landing-zone-full-automation` Q5), or `may react to the move` (an
   * authored movement reaction).
   */
  why: string;
};

/**
 * The newest outstanding move-checkpoint decision against `moverId`, as a decider line, or `null`
 * (none yet: the prediction runs before the server's decision lands, or the decider is hidden from
 * this client). `viewer` picks "your start square" over "<mover>'s start square": `"mover"` is the
 * client that dispatched the move (`MoveHoldBanner`); `{ slot }` is any other client
 * (`TablePausedBanner`), which reads "your" when its slot drives the mover ({@link
 * controlsCombatant}). That second case includes the mover's own client once the first watcher's
 * decline commits: the commit clears its predicted hold, so it falls back to the table banner
 * while the move still waits on the next watcher.
 */
export function deriveMoveHoldDecider(args: {
  decisions: Record<string, GameDecisionMessage>;
  state: EncounterState;
  moverId: string;
  viewer: "mover" | { slot: string | null };
}): MoveHoldDecider | null {
  const { decisions, state, moverId, viewer } = args;
  let latest: GameDecisionMessage | undefined;
  for (const d of Object.values(decisions)) {
    if (d.source === moverId && isMoveCheckpointPromptId(d.promptId)) latest = d;
  }
  if (latest === undefined) return null;
  const decider = state.combatants[latest.who];
  if (decider === undefined) return null;
  const mover = state.combatants[moverId];
  const moverName = mover?.name ?? moverId;
  const ownMove = viewer === "mover" || controlsCombatant(mover, viewer.slot);
  const whose = ownMove ? "your" : `${moverName}'s`;
  const consumer = moveCheckpointConsumerId(latest.promptId);
  const why =
    consumer === OVERWATCH_CHECKPOINT_ID
      ? `threatens ${whose} start square`
      : consumer === COVERING_FIRE_SPLIT_ID
        ? `holds ${whose} move for Covering Fire`
        : "may react to the move";
  return { deciderName: decider.name, why };
}
